
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import Hey from "@/lib/hey";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useAnalytics } from "@/hooks/use-analytics";

interface PasswordSectionProps {
  onToggleSection?: (isOpen: boolean) => void;
}

const PasswordSection = ({
  onToggleSection
}: PasswordSectionProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { trackProfileAction, trackError } = useAnalytics();

  const handleToggle = (open: boolean) => {
    setIsOpen(open);
    if (onToggleSection) {
      onToggleSection(open);
    }
  };

  const handlePasswordUpdate = async () => {
    if (newPassword !== confirmPassword) {
      trackProfileAction('change_password', 'failed', 'passwords_mismatch');
      toast({
        variant: "destructive",
        title: t("common.toast.error"),
        description: t("settings.passwordMismatch")
      });
      return;
    }
    
    if (newPassword.length < 8) {
      trackProfileAction('change_password', 'failed', 'password_too_short');
      toast({
        variant: "destructive",
        title: t("common.toast.error"),
        description: t("settings.passwordTooShort")
      });
      return;
    }
    
    try {
      setLoading(true);
      trackProfileAction('change_password', 'success', 'attempt');
      console.debug("📤 API REQUEST: updatePassword", {
        password: "******"
      });
      await Hey.HeyUtils.getInstance().updatePassword(newPassword);
      console.debug("📥 API RESPONSE: updatePassword success");
      trackProfileAction('change_password', 'success', 'completed');
      toast({
        title: t("common.toast.success"),
        description: t("settings.passwordUpdated")
      });
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("🚨 ERROR: Error updating password:", error);
      console.debug("📥 API RESPONSE: updatePassword failed", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      trackProfileAction('change_password', 'failed', error instanceof Error ? error.message : 'unknown_error');
      trackError('auth', `Password update failed: ${error instanceof Error ? error.message : "Unknown error"}`, 'PasswordSection');
      toast({
        variant: "destructive",
        title: t("common.toast.error"),
        description: error instanceof Error ? error.message : t("settings.updateError")
      });
    } finally {
      setLoading(false);
    }
  };
  
  return <div className="space-y-4">
      <Collapsible open={isOpen} onOpenChange={handleToggle} className="w-full">
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between bg-accent/50 p-3 rounded-md cursor-pointer">
            <h3 className="text-lg font-medium">{t("settings.changePassword")}</h3>
            <Button variant="outline" size="sm" className="border border-primary/30 shadow-sm" type="button" onClick={e => {
            e.preventDefault();
            handleToggle(!isOpen);
          }}>
              {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              <span className="sr-only">{isOpen ? "Close" : "Open"}</span>
            </Button>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-4 pt-4 animate-accordion-down">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="new-password">{t("settings.newPassword")}</Label>
              <Input 
                id="new-password" 
                type="password" 
                value={newPassword} 
                onChange={e => {
                  setNewPassword(e.target.value);
                  if (e.target.value && e.target.value.length > 0 && e.target.value.length < 8) {
                    trackProfileAction('change_password', 'failed', 'typing_password_too_short');
                  }
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirm-password">{t("settings.confirmPassword")}</Label>
              <Input 
                id="confirm-password" 
                type="password" 
                value={confirmPassword} 
                onChange={e => setConfirmPassword(e.target.value)} 
              />
            </div>
            <Button 
              onClick={handlePasswordUpdate} 
              disabled={loading} 
              style={{
                backgroundColor: "#000835"
              }} 
              className="bg-[#000835]"
            >
              {loading ? t("common.updating") : t("settings.updatePassword")}
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>;
};

export default PasswordSection;
